/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import _ from "lodash";
import { toHeaderCase } from "js-convert-case";
import moment from "moment";

import config from "../config/utilityCenterDetails.config.json";

import { useUtilityCenterId } from "../provider";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_UTILITY_CENTER_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { AppNavigatorService } from "../../appNavigatorService";
//@ts-ignore
import { v4 as uuidv4 } from "uuid";
import { Float } from "react-native/Libraries/Types/CodegenTypes";
import { DEFAULT_APP_CONFIG } from "../../../config/default.config";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IUtiltiyCenterDetailsMethods extends IPluginMethodMap {
  getUtilityCenterDetails(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export class UtilityCenterDetailsService implements IAgentPlugin {
  readonly methods: IUtiltiyCenterDetailsMethods;

  constructor() {
    this.methods = {
      getUtilityCenterDetails: this.getUtilityCenterDetails.bind(this),
    };
  }

  async getUtilityCenterDetails() {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { id } = useUtilityCenterId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_UTILITY_CENTER_DETAIL,
          variables: {
            filter: {
              _id: id,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getDistCent) {
          return {
            //logoSrc: "https://i.ibb.co/0fFrdXn/warehouse.png",
            title: this.formatTitle(res?.data?.getDistCent),
            // badgeTitle: this?.formatBadgeTitle(res?.data?.getDistCent),
            // badgeTitleBg: this.formatBadgeTitleBg(res?.data?.getDistCent),
            subtitle: this.formatSubTitle(res?.data?.getDistCent),
            location: res?.data?.getDistCent?.geoLocation?.coordinates,
            carouselItems: this.formatCarouselItems(res?.data?.getDistCent),
            //description: res?.data?.getDistCent?.description,
            //attributeItems: this.formatAttributeItems(res?.data?.getDistCent),
            listItems: this.formatListItem(res?.data?.getDistCent),
            //mapProps: this.formatMarkers(res?.data?.getDistCent),
            buttons: [
              {
                key: "edit",
                title: "Edit",
                color: "primary",
              },
            ],
            updateUtilityCenterTweetyProps: this.formatFormProps(
              config.UPDATE_DIST_CENTER_TWEETY_PROPS,
              res?.data?.getDistCent
            ),
            paymentCounters: this.formatAppCards(
              res?.data?.getDistCent,
              "payment"
            ),
            distCounters: this.formatAppCards(
              res?.data?.getDistCent,
              "distribution"
            ),
          };
        }
      }
    } catch (err: any) {
      return err;
    }
  }

  private formatAppCards = (distCenter: any, qType: string) => {
    let _res: any[] = [];
    let _counters =
      distCenter?.counters &&
      Array.isArray(distCenter?.counters) &&
      distCenter?.counters.length > 0
        ? distCenter?.counters.filter((item: any) =>
            item?.counterType.includes(qType)
          )
        : [];

    console.log(`_counters`, _counters);
    _counters &&
      _counters.length > 0 &&
      _counters.map((item: any, index: number) => {
        _res.push({
          id: item?.id,
          appCardId: item?.id,
          appCardImage: "",
          appCardTitle: item?.name,
          appCardList: [
            {
              name: "Counter No",
              value: item?.counterNo,
            },
          ],
        });
      });

    return _res;
    //  return {
    //   id: _data?.id,
    //   appCardId: _data?.masqID,
    //   appCardImage: _data?.user?.image,
    //   appCardTitle: _data?.user?.fullName,
    //   //appCardDescription: `${_data?.masqID} | ${_data?.costumeID}`,
    // appCardList: [
    //   {
    //     name: "Masq ID",
    //     value: _data?.masqID,
    //   },
    //   {
    //     name: "Costume ID",
    //     value: _data?.costumeID,
    //   },
    // ],
    // };
  };

  private formatListItem(distCenter: any) {
    const formatted: any = {
      info: config?.attributeItems,
    };
    const index = _.findIndex(
      formatted.info,
      (info: any) => info.key === "address"
    );
    if (index > -1) {
      formatted.info[index].subTitle = distCenter?.address ?? null;
    }

    return formatted?.info;
  }

  private formatFormProps(formProps: any, data: any) {
    let _res: any = formProps;
    let _lat = 0;
    let _lng = 0;
    if (
      data?.geoLocation &&
      data?.geoLocation?.coordinates &&
      Array.isArray(data?.geoLocation?.coordinates) &&
      data?.geoLocation?.coordinates.length === 2
    ) {
      _lat = data?.geoLocation?.coordinates[0];
      _lng = data?.geoLocation?.coordinates[1];
    }
    _res.initialData = {
      distCenterName: data?.name,
      latitude: _lat,
      longitude: _lng,
      address: data?.address,
    };
    console.log(`_res in formatFormProps`, _res);
    return _res;
  }

  private formatMarkers(distCenter: any) {
    let _result: any = {
      markers: [],
      center: [0, 0],
    };
    if (distCenter?.geoLocation) {
      let _coordinates = distCenter?.geoLocation?.coordinates;
      if (
        _coordinates &&
        Array.isArray(_coordinates) &&
        _coordinates.length === 2
      ) {
        _result.center = [_coordinates[0], _coordinates[1]];
        _result.markers.push({
          id: uuidv4(),
          latLng: [_coordinates[0], _coordinates[1]],
          name: distCenter?.name,
          items: [
            {
              icon: "LocationIcon",
              description: distCenter?.address,
            },
          ],
        });
      }
    }
    console.log("mapProps", _result);
    return _result;
  }

  private formatCarouselItems(distCenter: any) {
    const formatted: any = [];
    if (distCenter?.geoLocation) {
      let _coordinates = distCenter?.geoLocation?.coordinates;
      if (
        _coordinates &&
        Array.isArray(_coordinates) &&
        _coordinates.length === 2
      ) {
        formatted.push({
          type: "map",
          mapProps: this.formatMarkers(distCenter),
          accessKey: DEFAULT_APP_CONFIG?.leafletMap?.accessKey,
        });
      }
    }
    return formatted;
  }

  private formatTitle(distCenter: any) {
    let formatted = distCenter?.name ?? "";
    return toHeaderCase(formatted);
  }

  private formatSubTitle(distCenter: any) {
    let formatted = distCenter?.distCenterId ?? "";
    return toHeaderCase(formatted);
  }

  private formatImages(image?: string) {
    const formatted: any = [];
    if (image) {
      formatted.push({
        original: `data:image/png;base64,${image}`,
        thumbnail: `data:image/png;base64,${image}`,
      });
    }
    return formatted;
  }
}
